import React, { KeyboardEvent, useState, useEffect, useRef, useMemo, FunctionComponent } from 'react';
import lineApi from '../../../api/line';
import taskApi from '../../../api/tasks';
import { Table, IconButton, TableRow, TableCell } from '../../../tailwindcomponents';
import { LinePackageOverviewObj } from '../../../types/line';
import { LinePlannerFilters } from '../../../types/linePlanner';
import { Part } from '../../../types/tasks';
import { ShoppingCart, KeyboardArrowRight, StopCircle } from '../../icons';
import { redirectToMxi } from '../../../config/mxiProvider';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../reducer';
import { DateTime } from 'luxon';
import WorkPackageFilters from './WorkPackageFilters';
import WorkPackageActionButtons from './WorkPackageActionButtons';
import PartsDialog from './dialogs/PartsDialog';
import OutstandingPartsDialog from './dialogs/OutstandingPartsDialog';
import StoppedPackagesDialog from './dialogs/StoppedPackagesDialog';
import DateConflictDialog from './dialogs/DateConflictDialog';
import PriorityItemsDialog from './dialogs/PriorityItemsDialog';
import { redirectToIdmrWorkPackage } from '../../../config/idmrProvider';
import { formatManHours } from '../../../utils/dateUtils';
import WorkPackageTasksPanel from './WorkPackageTasksPanel';

const HEADERS = ["Amount", "MXI", "eSign", "Tail", "Work Package", "Work Type", "WO Number", "WO Status", "Start Date", "Due Date", "MH"];
const COLUMN_COUNT = HEADERS.length;

type LineWorkPackageOverviewProps = {
  allTails: Array<string>;
  setLoadingProp: Function;
};

const LineWorkPackageOverview: FunctionComponent<LineWorkPackageOverviewProps> = ({ 
  allTails, 
  setLoadingProp 
}) => {
  const dispatch = useDispatch();
  const store = useSelector((state: RootState) => state);
  
  const [reports, setReports] = useState(Array<LinePackageOverviewObj>());
  const [partsInOrder, setPartsInOrder] = useState(Array<Part>());
  const [unavailableParts, setUnAvalableParts] = useState(Array<Part>());
  const [deleteOutstandingPartsDialogOpen, setDeleteOutstandingPartsDialogOpen] = useState(false);
  const [stoppedPackages, setStoppedPackages] = useState(Array<LinePackageOverviewObj>());
  const [currentTaskName, setCurrentTaskName] = useState<string>("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [stoppedDialogOpen, setStoppedDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [startDateLaterThanDueDateOpen, setStartDateLaterThanDueDateOpen] = useState(false);
  const [rowClicked, setRowClicked] = useState<string | null>(null);
  const [priorityDialogOpen, setPriorityDialogOpen] = useState<boolean>(false);

  const refresh = () => {
    setLoadingProp(true);
    const filters = store.linePlanner.linePlannerfilters;
    
    lineApi.GetLinePacketsOverview(
      DateTime.fromISO(filters.dateFrom).toJSDate(),
      DateTime.fromISO(filters.dateTo).toJSDate(),
      filters.filterByStatus,
      filters.tail,
      filters.filterByHangers,
      filters.dueDateHourCycles,
      filters.dueDateExceedsFifteen,
      true,
      filters.filterByWorkType,
      filters.filterByAircraftTypes,
    ).then(res => {
      setReports(res.data);
      setLoadingProp(false);
    });
  };

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // R key to refresh, but not when typing in autocomplete
      if ((event.key === 'r' || event.key === 'R') && !document.activeElement?.classList.contains("MuiAutocomplete-input")) {
        event.preventDefault();
        refreshRef.current();
      }
    };

    refresh();
    document.addEventListener('keyup', handleKeyPress as any, false);
    return () => {
      document.removeEventListener('keyup', handleKeyPress as any, false);
    };
  }, []); // Only run once on mount

  const handleRowClicked = (woBarcode: string) => {
    if (rowClicked === woBarcode) {
      setRowClicked(null);
    } else {
      setRowClicked(woBarcode);
    }
  };

  const openDialog = (woBarcode: string, wo_name: string) => {
    setCurrentTaskName(wo_name);
    setDeleteDialogOpen(true);
    setPartsInOrder([]);
    taskApi.GetPartsByWoBarcode(woBarcode).then(res => {
      // A 401 body has no data; the Table would crash on undefined rows
      setPartsInOrder(res.data ?? []);
    });
  };

  const getOutstandingMaterials = () => {
    setDeleteOutstandingPartsDialogOpen(true);
    setUnAvalableParts([] as Array<Part>);
    setLoading(true);
    
    const currentTasks = reports.map(report => report.wo_barcode);
    const tempFromDate = DateTime.fromISO(store.linePlanner.linePlannerfilters.dateFrom).toJSDate();
    const tempToDate = DateTime.fromISO(store.linePlanner.linePlannerfilters.dateTo).toJSDate();

    lineApi.GetAllOutstandingMaterialsByWo(tempFromDate, tempToDate, currentTasks).then(res => {
      setLoading(false);
      if (res.status === true) {
        setUnAvalableParts(res.data);
      }
    });
  };

  const onPackageOnHold = () => {
    setStoppedPackages(reports.filter(s => s.is_wo_on_hold === true));
    setStoppedDialogOpen(true);
  };


  const handleWorkTypeUpdate = (filtersCopy: LinePlannerFilters, newWorkTypes: string[]) => {
      filtersCopy.filterByWorkType = newWorkTypes;
  };

  const updateFilters = (updates: Partial<LinePlannerFilters>) => {
    const filtersCopy: LinePlannerFilters = {
      ...store.linePlanner.linePlannerfilters,
    };

    // Handle simple updates
    if (updates.dateFrom !== undefined) {
      filtersCopy.dateFrom = updates.dateFrom;
    }
    if (updates.dateTo !== undefined) {
      filtersCopy.dateTo = updates.dateTo;
    }
    if (updates.tail !== undefined) {
      filtersCopy.tail = updates.tail as string;
    }
    if (updates.filterByHangers !== undefined) {
      filtersCopy.filterByHangers = updates.filterByHangers;
    }
    if (updates.filterByStatus !== undefined) {
      filtersCopy.filterByStatus = updates.filterByStatus;
    }
    if (updates.dueDateHourCycles !== undefined) {
      filtersCopy.dueDateHourCycles = updates.dueDateHourCycles;
    }
    if (updates.dueDateExceedsFifteen !== undefined) {
      filtersCopy.dueDateExceedsFifteen = updates.dueDateExceedsFifteen;
    }

    // Handle work type with special logic
    if (updates.filterByWorkType !== undefined) {
      handleWorkTypeUpdate(filtersCopy, updates.filterByWorkType);
    }

    if (updates.filterByAircraftTypes !== undefined) {
      filtersCopy.filterByAircraftTypes = updates.filterByAircraftTypes;
    }

    dispatch({
      type: "LINE_PLANNER_WORK_PACKAGE_OVERVIEW_FILTERS",
      linePlannerfilters: filtersCopy,
    });
  };

  const isSameDay = (date1: string, date2: string): boolean => {
    const d1 = DateTime.fromISO(date1).minus({ hours: 6 });
    const d2 = DateTime.fromISO(date2).minus({ hours: 6 });
    return d1.hasSame(d2, 'day');
  };

  const formatCustomDate = (isoDate: string): string => {
    return DateTime.fromISO(isoDate).minus({ hours: 6 }).toFormat("cccc d.LLLL yyyy");
  };

  const shiftTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const r of reports) {
      const day = DateTime.fromISO(r.start_date.toString()).minus({ hours: 6 }).toISODate();
      const key = `${day}|${r.wo_status_of_the_day}`;
      totals.set(key, (totals.get(key) ?? 0) + (r.mxi_man_hours ?? 0));
    }
    return totals;
  }, [reports]);

  const generateTableBody = (row: LinePackageOverviewObj, index: number, rowsInView: Array<LinePackageOverviewObj> = reports) => {
    let taskPriority = "";
    if (row.task_priority === "P01") {
      taskPriority = "P1";
    } else if (row.task_priority === "P02") {
      taskPriority = "P2";
    } else if (row.task_priority === "P03") {
      taskPriority = "P3";
    }

    const currentDate = row.start_date.toString();
    const currentShift = row.wo_status_of_the_day;
    const previousDate = index > 0 ? rowsInView[index - 1]?.start_date?.toString() : null;
    const previousShift = index > 0 ? rowsInView[index - 1]?.wo_status_of_the_day : null;

    const dayChanged = previousDate ? !isSameDay(currentDate, previousDate) : true;
    const shiftChanged = !dayChanged && previousShift !== currentShift;
    const showDivider = dayChanged || shiftChanged;
    const isExpanded = row.wo_barcode === rowClicked;

    const shiftTotalMH = showDivider
      ? shiftTotals.get(`${DateTime.fromISO(currentDate).minus({ hours: 6 }).toISODate()}|${currentShift}`) ?? 0
      : 0;

    const shiftLabel = currentShift === "Day" ? "Day shift" : currentShift === "Night" ? "Night shift" : "";

    const divider = showDivider ? (
      <TableRow key={`divider-${index}`}>
        <TableCell colSpan={COLUMN_COUNT} className="bg-background font-bold align-middle">
          <div className="flex items-center">
            <span className="flex-1"></span>
            <span className="flex-1 text-center">{formatCustomDate(currentDate)}</span>
            <span className="flex-1 text-right">
              {shiftTotalMH > 0 && (
                <span className="bg-primary text-[color:var(--ds-accent-fg)] px-3 py-1 rounded text-sm font-bold">
                  {shiftLabel && `${shiftLabel} — `}{formatManHours(shiftTotalMH)}
                </span>
              )}
            </span>
          </div>
        </TableCell>
      </TableRow>
    ) : null;
    return (
      <React.Fragment key={index}>
        {divider}
        <TableRow
          className={
            row.start_date_bigger_than_due_date === true ? "ds-row-tint bg-red-500" :
            row.wo_status_of_the_day === "Day" ? "ds-row-tint bg-shift-day" :
            row.wo_status_of_the_day === "Night" ? "ds-row-tint bg-shift-night" : ""
          }
        >
          <TableCell>
            <div className="flex flex-col">
              {row.task_count !== 1 && (
                <div>
                  <IconButton
                    aria-label="expand row"
                    aria-expanded={isExpanded}
                    size="small"
                    className="hover:bg-transparent"
                    onClick={() => handleRowClicked(row.wo_barcode)}
                  >
                    <KeyboardArrowRight className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </IconButton>
                </div>
              )}

              <div className="flex flex-nowrap gap-1 items-center">
                {(row.basket !== 0 && row.basket !== row.avail_basket) && (
                  <IconButton
                    size="small"
                    className="hover:bg-transparent"
                    style={row.start_date_bigger_than_due_date ? { color: "white", backgroundColor: "black" } : { backgroundColor: "red" }}
                    onClick={() => openDialog(row.wo_barcode, row.wo_name)}
                  >
                    <ShoppingCart />
                  </IconButton>
                )}
                {(row.basket !== 0 && row.basket === row.avail_basket) && (
                  <IconButton size="small" className="hover:bg-transparent" onClick={() => openDialog(row.wo_barcode, row.wo_name)}>
                    <ShoppingCart />
                  </IconButton>
                )}

                {row.is_wo_on_hold === true && (row.basket !== 0 && row.basket !== row.avail_basket) && (
                  <IconButton
                    size="small"
                    className="hover:bg-transparent"
                    style={row.start_date_bigger_than_due_date ? { color: "white", backgroundColor: "black" } : { backgroundColor: "red" }}
                    onClick={() => onPackageOnHold()}
                  >
                    <StopCircle />
                  </IconButton>
                )}
                {row.is_wo_on_hold === true && (row.basket !== 0 && row.basket === row.avail_basket) && (
                  <IconButton size="small" className="hover:bg-transparent" onClick={() => onPackageOnHold()}>
                    <StopCircle />
                  </IconButton>
                )}

                {(row.is_wo_on_hold === true && row.basket === 0) && (
                  <IconButton size="small" className="hover:bg-transparent" onClick={() => onPackageOnHold()}>
                    <StopCircle />
                  </IconButton>
                )}

                {taskPriority !== "" && (
                  <div className="flex items-center shrink-0">
                    <div className="w-[18px] h-[18px] bg-red-500 text-white font-bold text-[10px] flex items-center justify-center rounded-full text-center shrink-0" style={row.start_date_bigger_than_due_date ? { color: "white", backgroundColor: "black" } : { backgroundColor: "red" }}>
                      {taskPriority}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TableCell>

          <TableCell onClick={() => redirectToMxi(row.wo_barcode)}>
            <IconButton size="small" className="px-1 w-auto hover:bg-transparent" style={{ fontSize: 10 }}>MXI</IconButton>
          </TableCell>
          <TableCell onClick={() => redirectToIdmrWorkPackage(row.wo_barcode)}>
            <IconButton size="small" className="px-1 w-auto hover:bg-transparent" style={{ fontSize: 10 }}>eSign</IconButton>
          </TableCell>

          <TableCell className="w-[80px]">{row.tail_number}</TableCell>
          <TableCell>{row.wo_name}</TableCell>
          <TableCell>{row.work_type_cd}</TableCell>
          <TableCell>{(row.wo_number ?? '').replace('WO - ', '')}</TableCell>
          <TableCell>
            <span
              style={{
                backgroundColor: row.wo_status === "IN WORK" ? "var(--ds-accent)" : "transparent",
                color: row.wo_status === "IN WORK" ? "var(--ds-accent-fg)" : "inherit",
                padding: row.wo_status === "IN WORK" ? "6px 12px" : 0,
                borderRadius: row.wo_status === "IN WORK" ? "16px" : 0,
                display: "inline-block",
                fontWeight: row.wo_status === "IN WORK" ? "500" : "normal",
              }}
            >
              {row.wo_status}
            </span>
          </TableCell>
          <TableCell>{row.start_date_text}</TableCell>
          <TableCell>{row.due_date_text}</TableCell>
          <TableCell>
            {formatManHours(row.mxi_man_hours)}
          </TableCell>
        </TableRow>
        {(row.extra_tasks && isExpanded) && (
          <WorkPackageTasksPanel tasks={row.extra_tasks} colSpan={COLUMN_COUNT} />
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="w-full flex flex-col">
      <WorkPackageFilters
        allTails={allTails}
        filters={store.linePlanner.linePlannerfilters}
        onFilterChange={updateFilters}
        onRefresh={refresh}
      />

      <PartsDialog
        visible={deleteDialogOpen}
        taskName={currentTaskName}
        parts={partsInOrder}
        onClose={() => setDeleteDialogOpen(false)}
      />

      <OutstandingPartsDialog
        visible={deleteOutstandingPartsDialogOpen}
        parts={unavailableParts}
        loading={loading}
        onClose={() => setDeleteOutstandingPartsDialogOpen(false)}
      />

      <StoppedPackagesDialog
        visible={stoppedDialogOpen}
        packages={stoppedPackages}
        onClose={() => setStoppedDialogOpen(false)}
      />

      <DateConflictDialog
        visible={startDateLaterThanDueDateOpen}
        reports={reports}
        headers={HEADERS}
        generateTableBody={generateTableBody}
        onClose={() => setStartDateLaterThanDueDateOpen(false)}
      />

      <PriorityItemsDialog
        visible={priorityDialogOpen}
        reports={reports}
        headers={HEADERS}
        generateTableBody={generateTableBody}
        onClose={() => setPriorityDialogOpen(false)}
      />

      <WorkPackageActionButtons
        reports={reports}
        onShowOutstandingMaterials={getOutstandingMaterials}
        onShowPackagesOnHold={onPackageOnHold}
        onShowDateConflicts={() => setStartDateLaterThanDueDateOpen(true)}
        onShowPriorityItems={() => setPriorityDialogOpen(true)}
      />

      <div className="[&_.ds-table_thead_th]:!px-2 [&_.ds-table_tbody_td]:!px-2 [&_.ds-table_tbody_td]:!py-1">
        <Table
          headers={HEADERS}
          rows={reports}
          generateBody={(row: any, index: number) => generateTableBody(row, index)}
          pagination={true}
          defaultRowsPerPage={100}
          skipIndexCol={true}
        />
      </div>
    </div>
  );
};

export default LineWorkPackageOverview;
